/*
 * Inspector panel, sections and dialogs (W2-06). Design tokens only (src/ui/styles/tokens.css); state is never
 * shown by colour alone (icons and words accompany it). The panel is 320 px wide in the Compose layout and a
 * drawer in the Laptop layout: every row wraps instead of overflowing.
 */

/* ---------------------------------------------------------------- panel */

.panel {
  container: inspector / inline-size;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--color-surface-1);
  color: var(--color-text);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  min-height: var(--control-h-lg);
}

.headerTitle {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerActions {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--space-2) var(--space-3) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.empty {
  padding: var(--space-5) var(--space-3);
}

/* ---------------------------------------------------------------- rows */

.title {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-width: 0;
}

.titleDetail {
  font-size: var(--text-xs);
  font-weight: var(--weight-normal);
  color: var(--color-text-subtle);
  text-transform: none;
  letter-spacing: 0;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.row {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.withInfo {
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
  min-width: 0;
}

.grow {
  flex: 1 1 auto;
  min-width: 0;
}

.inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-width: 0;
}

/*
 * Two fields side by side when each still fits its label column plus a usable input (about 112 px), else one
 * row per field. A label-left field never shares a narrow track: the label would take it and the input would
 * collapse to a few pixels (UX-049).
 */
.pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, calc(var(--label-width) + 136px)), 1fr));
  gap: var(--space-1) var(--space-3);
}

/* Two fields with their labels stacked above the inputs (`stacked`): side by side down to 112 px each. */
.pairCompact {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 112px), 1fr));
  gap: var(--space-1) var(--space-3);
  align-items: start;
}

.readout {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
  line-height: var(--leading-normal);
  overflow-wrap: anywhere;
}

.mono {
  font-family: var(--font-mono);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.subheading {
  margin: var(--space-2) 0 0;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* A checkbox whose long label wraps next to its box, not under it (UX-049: "Stay, then jump ..."). */
.fields .checkInline {
  flex-wrap: nowrap;
  align-items: flex-start;
}

.fields .checkInline > label {
  flex: 1 1 auto;
}

/* A colour field with its label above: "None (default)" fits instead of "None (def..." (UX-057). */
.fields .colorStacked {
  grid-template-columns: minmax(0, 1fr);
}

/* ---------------------------------------------------------------- callouts */

.callout {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: var(--border-width) solid var(--color-border);
  background: var(--color-surface-2);
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  color: var(--color-text);
}

.callout[data-tone='info'] {
  border-color: var(--color-info-muted);
  background: var(--color-info-muted);
}

.callout[data-tone='warning'] {
  border-color: var(--color-warning);
  background: var(--color-warning-muted);
}

.callout[data-tone='danger'] {
  border-color: var(--color-danger);
  background: var(--color-danger-muted);
}

.callout[data-tone='success'] {
  border-color: var(--color-success);
  background: var(--color-success-muted);
}

.calloutIcon {
  display: inline-flex;
  padding-top: 1px;
}

.callout[data-tone='info'] .calloutIcon {
  color: var(--color-info);
}

.callout[data-tone='warning'] .calloutIcon {
  color: var(--color-warning);
}

.callout[data-tone='danger'] .calloutIcon {
  color: var(--color-danger);
}

.callout[data-tone='success'] .calloutIcon {
  color: var(--color-success);
}

.calloutText {
  flex: 1 1 160px;
  min-width: 0;
  overflow-wrap: anywhere;
}

.calloutActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

/* ---------------------------------------------------------------- diagrams */

.diagram {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  color: var(--color-text-subtle);
}

.diagramFrame {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-0);
  font-size: var(--text-2xs);
  color: var(--color-text-subtle);
}

.diagramFrame svg {
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.dParent {
  stroke: var(--color-text-muted);
  stroke-width: 2.5;
  stroke-linecap: round;
}

.dPivot {
  fill: var(--color-accent);
}

.dLink {
  stroke: var(--color-border-strong);
  stroke-width: 1;
  stroke-dasharray: 3 2;
}

.dChild {
  stroke: var(--color-accent);
  stroke-width: 2.5;
  stroke-linecap: round;
}

.dChildDot {
  fill: var(--color-accent);
}

.dNote {
  fill: var(--color-note-tap);
}

.dNoteHidden {
  fill: none;
  stroke: var(--color-note-tap);
  stroke-width: 1;
  stroke-dasharray: 2 2;
}

.dArrow {
  stroke: var(--color-text-subtle);
  stroke-width: 1;
}

/* ---------------------------------------------------------------- properties list */

.propList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.propItem {
  display: grid;
  grid-template-columns: minmax(84px, auto) 1fr auto;
  align-items: start;
  gap: var(--space-2);
  padding: var(--space-1) 0;
  border-bottom: var(--border-width) solid var(--color-divider);
  font-size: var(--text-xs);
}

.propName {
  font-weight: var(--weight-medium);
  color: var(--color-text);
}

.propState {
  color: var(--color-text-muted);
  overflow-wrap: anywhere;
}

.propState[data-absent='true'] {
  color: var(--color-text);
}

/* RPE-only fields: name and value, the badges on a line of their own (UX-060). */
.rpeOnlyItem {
  display: grid;
  grid-template-columns: minmax(84px, auto) minmax(0, 1fr);
  align-items: start;
  gap: var(--space-1) var(--space-2);
  padding: var(--space-1) 0;
  border-bottom: var(--border-width) solid var(--color-divider);
  font-size: var(--text-xs);
}

.rpeOnlyItem dd {
  margin: 0;
}

.rpeOnlyBadges {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: center;
}

/* ---------------------------------------------------------------- distance-effect graph */

.graph {
  width: 100%;
  height: auto;
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.graphGrid {
  stroke: var(--color-curve-guide);
  stroke-width: 1;
}

.graphOne {
  stroke: var(--color-border-strong);
  stroke-width: 1;
  stroke-dasharray: 4 3;
}

.graphCurve {
  fill: none;
  stroke: var(--color-curve);
  stroke-width: 2;
}

.graphCurve[data-inert='true'] {
  stroke: var(--color-text-subtle);
  stroke-dasharray: 5 3;
}

.graphPoint {
  fill: var(--color-surface-1);
  stroke: var(--color-curve);
  stroke-width: 2;
}

.graphLabel {
  fill: var(--color-text-subtle);
  font-size: 9px;
  font-family: var(--font-sans);
}

.points {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

/* A distance-effect point: distance, curve (thumbnail button), value, remove. */
.point {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) auto minmax(0, 1fr) auto;
  align-items: end;
  gap: var(--space-1) var(--space-2);
}

.pointCurve,
.pointInf {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-1) 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.pointCurveButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--control-h-md);
  min-width: var(--control-h-md);
  padding: 0 var(--space-1);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-3);
  color: var(--color-text);
  cursor: pointer;
}

.pointCurveButton:hover {
  border-color: var(--color-border-strong);
  background: var(--color-surface-hover);
}

.pointCurveButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The far sentinel (9999999 px): shown as infinity, not a clipped number. */
.pointInfValue {
  display: flex;
  align-items: center;
  height: var(--control-h-md);
  padding: 0 var(--space-2);
  border: var(--border-width) dashed var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: var(--text-md);
}

.graphFar {
  fill: var(--color-curve);
}

.controlBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
}

.controlHead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

.controlHead > :first-child {
  flex: 1 1 auto;
}

/* ---------------------------------------------------------------- text preview strip */

.strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--space-1);
}

.stripCell {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  padding: var(--space-1);
  border-radius: var(--radius-xs);
  background: var(--color-surface-sunken);
  font-size: var(--text-2xs);
  color: var(--color-text-subtle);
  min-width: 0;
}

.stripText {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--color-text);
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------------------------------------------------------------- dialogs */

.dialogBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-height: min(70vh, 640px);
  overflow-y: auto;
}

.dialogSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.choiceList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  border: 0;
}

.choice {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-1) var(--space-2);
  align-items: start;
  padding: var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--text-sm);
}

.choice:has(input:checked) {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.choice input {
  margin: 2px 0 0;
  accent-color: var(--color-accent);
}

.choice input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.choiceDesc {
  grid-column: 2;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.confirmBox {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-accent-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
}

.badgeLine {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: center;
}

.popoverBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 220px;
  max-width: 300px;
  padding: var(--space-1);
}

/* ---------------------------------------------------------------- parent picker (large charts) */

.parentValue {
  max-width: 16ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.parentPopover {
  width: min(92vw, 300px);
}

.parentPicker {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.parentList {
  display: flex;
  flex-direction: column;
  max-height: 260px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.parentItem {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}

.parentItem:hover {
  background: var(--color-surface-hover);
}

.parentItem:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: calc(-1 * var(--focus-ring-width));
}

.parentItem[aria-pressed='true'] {
  background: var(--color-accent-muted);
  font-weight: var(--weight-semibold);
}

.parentItem[aria-disabled='true'] {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

.parentBlocked {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}
